import {
  type FC,
  type ComponentPropsWithoutRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Check,
  Download,
  File,
  FileText,
  MapPin,
  MessageCircle,
  Pencil,
  Printer,
} from "lucide-react";

import { SupportAvatars } from "@/shared/components/SupportAvatars";
import { useIntercom } from "@/shared/providers/IntercomProvider";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import type { EstateItem } from "@/shared/types/estateTypes";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { cn } from "@/shared/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { parseMarkdownSections } from "@/shared/utils/parseMarkdownSections";
import { trackEvent, EventType } from "@/shared/utils/core";

import { generateGuidePdf, printGuidePdf } from "../utils/generateGuidePdf";
import { getVehicleLabel } from "../utils/vehicleUtils";

interface GuideRendererProps {
  markdown: string;
  moduleName: string;
  deceasedFirstName: string;
  deceasedFullName: string;
  requestorFullName: string;
  onEdit: () => void;
  onMarkComplete?: () => void;
  isComplete?: boolean;
  illustration?: string;
  heroBg?: string;
  vehicleItems?: EstateItem[];
  selectedVehicleId?: string;
  onVehicleChange?: (vehicleId: string) => void;
  bannerLabel?: string;
  bannerTags?: { icon: "location" | "file"; label: string }[];
  formLibrary?: { type: "file" | "link"; name: string; href: string }[];
  additionalResources?: { name: string; href: string }[];
}

export const GuideRenderer: FC<GuideRendererProps> = ({
  markdown,
  moduleName,
  deceasedFirstName,
  deceasedFullName,
  requestorFullName,
  onEdit,
  onMarkComplete,
  isComplete,
  illustration,
  heroBg = "bg-blue-50",
  vehicleItems,
  selectedVehicleId,
  onVehicleChange,
  bannerLabel,
  bannerTags,
  formLibrary,
  additionalResources,
}) => {
  const { openSidePanel } = useIntercom();
  const showVehicleDropdown = vehicleItems && vehicleItems.length > 1 && onVehicleChange;

  const sections = useMemo(() => parseMarkdownSections(markdown), [markdown]);

  const allSectionIds = useMemo(() => sections.map((s) => s.id), [sections]);

  const [openSections, setOpenSections] = useState<string[]>(allSectionIds.slice(0, 1));
  const [activeSection, setActiveSection] = useState<string>(allSectionIds[0] ?? "");
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const guideCardRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    const container = contentRef.current?.closest(".overflow-y-auto");
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace("section-", "");
            setActiveSection(id);
            break;
          }
        }
      },
      { root: container, rootMargin: "-80px 0px -60% 0px", threshold: 0 },
    );

    for (const section of sections) {
      const el = document.getElementById(`section-${section.id}`);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sections]);

  const handleTocClick = useCallback((sectionId: string) => {
    setOpenSections((prev) => (prev.includes(sectionId) ? prev : [...prev, sectionId]));
    requestAnimationFrame(() => {
      const el = document.getElementById(`section-${sectionId}`);
      if (!el) return;
      const scrollContainer = el.closest(".overflow-y-auto");
      if (!scrollContainer) return;
      const containerRect = scrollContainer.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const offset = elRect.top - containerRect.top + scrollContainer.scrollTop - 64;
      scrollContainer.scrollTo({ top: offset, behavior: "smooth" });
    });
  }, []);

  const toggleChecked = useCallback((key: string) => {
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const remarkPlugins = useMemo(() => [remarkGfm], []);

  const markdownComponents: ComponentPropsWithoutRef<typeof Markdown>["components"] = useMemo(
    () => ({
      h2: ({ children, ...props }) => (
        <h2 {...props} className="font-sans text-base font-semibold text-primary mt-6 mb-2">
          {children}
        </h2>
      ),
      h3: ({ children, ...props }) => (
        <h3 {...props} className="font-sans text-sm font-semibold text-primary mt-4 mb-1">
          {children}
        </h3>
      ),
      input: ({ type, checked, ...props }) => {
        if (type === "checkbox") return null;
        return <input type={type} checked={checked} {...props} />;
      },
      li: ({ children, node, ...props }) => {
        const inputChild = node?.children?.find(
          (child) => child.type === "element" && child.tagName === "input",
        );
        const isTaskItem =
          inputChild && inputChild.type === "element" && inputChild.properties?.type === "checkbox";

        if (isTaskItem) {
          const text = String(children ?? "");
          const key = text.trim().slice(0, 60);
          const checked = checkedItems[key] ?? false;

          return (
            <li {...props} className="flex items-start gap-4 list-none -ml-6 my-1.5">
              <Checkbox
                checked={checked}
                onCheckedChange={() => toggleChecked(key)}
                className="mt-0.5"
              />
              <span className={`ml-1 ${checked ? "line-through text-tertiary" : ""}`}>
                {children}
              </span>
            </li>
          );
        }

        return <li {...props}>{children}</li>;
      },
      a: ({ children, href, ...props }) => (
        <a
          {...props}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand underline hover:text-brand/80 transition-colors"
        >
          {children}
        </a>
      ),
      table: ({ children, ...props }) => (
        <table
          {...props}
          className="w-full border-collapse border border-primary rounded-md text-sm"
        >
          {children}
        </table>
      ),
      thead: ({ children, ...props }) => (
        <thead {...props} className="bg-subtle">
          {children}
        </thead>
      ),
      th: ({ children, ...props }) => (
        <th
          {...props}
          className="border border-primary px-3 py-2 text-left text-2xs font-semibold text-primary"
        >
          {children}
        </th>
      ),
      td: ({ children, ...props }) => (
        <td {...props} className="border border-primary px-3 py-3 min-h-[2.5rem] text-secondary">
          {children || <span className="invisible">-</span>}
        </td>
      ),
    }),
    [checkedItems, toggleChecked],
  );

  const nameAlreadyPersonalized =
    deceasedFirstName && moduleName.toLowerCase().includes(deceasedFirstName.toLowerCase());
  const guideTitle = nameAlreadyPersonalized
    ? `${moduleName} guide`
    : deceasedFirstName
      ? `${deceasedFirstName}'s ${moduleName.toLowerCase()} guide`
      : `${moduleName} guide`;

  const handlePrint = useCallback(async () => {
    trackEvent(EventType.ESSENTIALS_GUIDE_PRINTED, { module_name: moduleName });
    try {
      await printGuidePdf({
        title: guideTitle,
        preparedFor: requestorFullName || "N/A",
        sections,
      });
    } catch (error) {
      console.error("Failed to print PDF:", error);
    }
  }, [guideTitle, requestorFullName, sections, moduleName]);

  const handleDownloadPdf = useCallback(async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    trackEvent(EventType.ESSENTIALS_PDF_DOWNLOADED, { module_name: moduleName });
    try {
      await generateGuidePdf({
        title: guideTitle,
        preparedFor: requestorFullName || "N/A",
        sections,
      });
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [guideTitle, requestorFullName, sections, isGeneratingPdf, moduleName]);

  const today = useMemo(() => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  return (
    <div className="flex gap-4 w-full">
      {/* Table of Contents Sidebar */}
      <nav className="hidden lg:block w-[20%] shrink-0 sticky top-24 self-start">
        <p className="text-sm text-secondary font-semibold mb-4">Contents</p>
        <div className="border-l border-tertiary" ref={contentRef}>
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => handleTocClick(section.id)}
                className={cn(
                  "text-sm transition-colors block py-2 pl-5 -ml-px border-l-2 text-left",
                  isActive
                    ? "text-primary border-brand"
                    : "text-secondary border-transparent hover:text-primary",
                )}
              >
                {section.title}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Personalization Banner */}
        <div className="rounded-lg border border-primary bg-white p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center rounded-sm border border-primary bg-white p-2">
                  <Check className="size-4 text-secondary" />
                </div>
                <p className="text-sm text-primary font-medium">
                  {bannerLabel ?? `Guide personalized for ${deceasedFullName || "your loved one"}`}
                </p>
              </div>
              {bannerTags && bannerTags.length > 0 && (
                <div className="flex items-center gap-3 pl-10">
                  {bannerTags.map((tag) => (
                    <div
                      key={tag.label}
                      className="flex items-center gap-1 text-2xs text-secondary"
                    >
                      {tag.icon === "location" && <MapPin className="size-3" />}
                      {tag.icon === "file" && <FileText className="size-3" />}
                      {tag.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="size-3.5" />
              Edit
            </Button>
          </div>
          {showVehicleDropdown && (
            <div className="mt-3 pt-3 border-t border-primary">
              <p className="text-sm text-secondary mb-2">Selected vehicle</p>
              <Select value={selectedVehicleId} onValueChange={onVehicleChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleItems.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {getVehicleLabel(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Guide Card */}
        <div
          ref={guideCardRef}
          className="rounded-lg border border-primary bg-white overflow-hidden"
        >
          {illustration && (
            <div className={cn("h-[180px] py-4 flex items-center justify-center", heroBg)}>
              <img
                src={illustration}
                className="h-[112px] w-[112px] object-contain"
                alt="Guide illustration"
              />
            </div>
          )}

          {/* Guide Header */}
          <div className="px-4 pt-6 pb-4 sm:px-8 sm:pt-8 sm:pb-6">
            <h2>{guideTitle}</h2>
            <TooltipProvider>
              <div className="flex items-center gap-1 mt-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handlePrint}
                      className="flex items-center justify-center h-10 w-10 rounded-lg border border-primary text-secondary hover:text-primary hover:bg-subtle transition-colors"
                    >
                      <Printer className="size-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Print this guide</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      disabled={isGeneratingPdf}
                      className="flex items-center justify-center h-10 w-10 rounded-lg border border-primary text-secondary hover:text-primary hover:bg-subtle transition-colors disabled:opacity-50"
                    >
                      <Download className="size-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Download PDF</TooltipContent>
                </Tooltip>
                {onMarkComplete && (
                  <Button variant="outline" onClick={onMarkComplete}>
                    <Check className="size-3.5" />
                    {isComplete ? "Completed" : "Mark as Complete"}
                  </Button>
                )}
              </div>
            </TooltipProvider>
            <p className="text-secondary mt-2">
              Prepared for: {requestorFullName || "N/A"} &bull; {today}
            </p>

            <button
              type="button"
              onClick={openSidePanel}
              className="mt-4 flex w-full items-center gap-3 rounded-lg border border-primary bg-white px-3 py-2.5 sm:px-4 sm:py-3 transition-colors hover:bg-background"
            >
              <SupportAvatars size="sm" showOnline />
              <p className="flex-1 min-w-0 text-sm text-left">
                <span className="font-semibold">Questions?</span>{" "}
                <span className="text-secondary">Our team can walk you through it.</span>
              </p>
              <MessageCircle className="size-4 text-secondary shrink-0" />
            </button>
          </div>

          {/* Accordion Sections */}
          <div className="px-4 sm:px-8">
            <Accordion type="multiple" value={openSections} onValueChange={setOpenSections}>
              {sections.map((section, index) => (
                <AccordionItem
                  key={section.id}
                  value={section.id}
                  id={`section-${section.id}`}
                  className={index === sections.length - 1 ? "border-b-0" : undefined}
                >
                  <AccordionTrigger className="text-left text-lg font-medium py-6 text-primary">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="prose prose-sm max-w-none text-secondary leading-relaxed [&_strong]:font-semibold [&_p]:leading-relaxed [&_li]:leading-relaxed">
                      <Markdown remarkPlugins={remarkPlugins} components={markdownComponents}>
                        {section.content}
                      </Markdown>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {formLibrary && formLibrary.length > 0 && (
          <div className="mt-6 rounded-lg border border-primary bg-white p-4 sm:p-8">
            <h3 className="text-lg font-semibold mb-4">Form library</h3>
            <div className="flex flex-col">
              {formLibrary.map((form) =>
                form.type === "file" ? (
                  <div
                    key={`${form.href}-${form.name}`}
                    className="flex items-center justify-between border-t border-primary py-4"
                  >
                    <div className="flex items-center gap-3">
                      <File
                        className="h-[27px] w-[21px] shrink-0 text-stone-200"
                        strokeWidth={1.5}
                      />
                      <p className="text-sm">{form.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={form.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center h-8 w-8 rounded-md text-secondary hover:text-primary hover:bg-subtle transition-colors"
                        aria-label={`Print ${form.name}`}
                      >
                        <Printer className="size-4" />
                      </a>
                      <a
                        href={form.href}
                        download
                        className="flex items-center justify-center h-8 w-8 rounded-md text-secondary hover:text-primary hover:bg-subtle transition-colors"
                        aria-label={`Download ${form.name}`}
                      >
                        <Download className="size-4" />
                      </a>
                    </div>
                  </div>
                ) : (
                  <div key={`${form.href}-${form.name}`} className="border-t border-primary py-4">
                    <a
                      href={form.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand hover:underline"
                    >
                      {form.name}
                    </a>
                  </div>
                ),
              )}
            </div>
          </div>
        )}
        {additionalResources && additionalResources.length > 0 && (
          <div className="mt-6 rounded-lg border border-primary bg-white p-4 sm:p-8">
            <h3 className="text-lg font-semibold mb-4">Additional Information and Resources</h3>
            <ul className="list-disc pl-5 space-y-1">
              {additionalResources.map((link) => (
                <li key={link.href + link.name}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-brand hover:underline"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Disclaimer */}
        <p className="mt-4 text-2xs text-secondary">
          Disclaimer: This guide is provided by Elayne for general informational purposes and
          convenience only. It does not constitute legal, financial, or professional advice. We
          recommend confirming all details directly before visiting. Every estate situation is
          unique. If you have questions about your legal authority or obligations, please consult a
          qualified attorney.
        </p>
      </div>
    </div>
  );
};
